/** CSV export modal — templates, columns, download. For Media Devoted | Created By Ciro Autiero */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { normalizeExportColumnOrder } from '../csvExportColumnOrder';
import { useCsvExportModalTemplates } from './templates';
import { useCsvExportModalCrossSourceState } from './useCsvExportModalCrossSourceState';
import { useCsvExportServerSortInit } from './useCsvExportServerSortInit';
import { runCsvExportModalDownload } from './run';
import type { UseCsvExportModalStateParams } from './useCsvExportModalState.types';

export function useCsvExportModalState({
  open,
  tab,
  columns,
  rows,
  userId,
  onOpenChange,
  serverCsvExport,
  serverCsvSortConfig,
  csvExportWorkDate = '',
  canManageTemplates = true,
  downloadFilenameInputRef,
  controlledExcludeCrossSourceNotedRows,
  onControlledExcludeCrossSourceNotedRowsChange,
}: UseCsvExportModalStateParams) {
  const allColumnKeys = columns.map((c) => c.key);
  const columnKeySig = allColumnKeys.join('\u0001');

  const [exportColumnKeys, setExportColumnKeys] = useState<string[]>(() => {
    const keys = columns.map((c) => c.key);
    return normalizeExportColumnOrder(keys, keys);
  });

  const [exportDownloadBaseName, setExportDownloadBaseName] = useState('');
  const [exportDownloadFilenameTouched, setExportDownloadFilenameTouched] =
    useState(false);
  const [serverExporting, setServerExporting] = useState(false);
  const wasOpenRef = useRef(false);

  useLayoutEffect(() => {
    if (open && !wasOpenRef.current) {
      setExportDownloadBaseName('');
      setExportDownloadFilenameTouched(false);
    }
    wasOpenRef.current = open;
  }, [open]);

  const onExportDownloadBaseNameChange = useCallback((v: string) => {
    setExportDownloadFilenameTouched(true);
    setExportDownloadBaseName(v);
  }, []);
  const [excludeCrossSourceNotedRows, setExcludeCrossSourceNotedRows] =
    useCsvExportModalCrossSourceState({
      open,
      exportColumnKeys,
      serverCsvExport,
      controlledExcludeCrossSourceNotedRows,
      onControlledExcludeCrossSourceNotedRowsChange,
    });

  const tpl = useCsvExportModalTemplates({
    open,
    tab,
    userId,
    canManageTemplates,
    columnKeySig,
    columns,
    allColumnKeys,
    exportColumnKeys,
    exportDownloadBaseName,
    exportDownloadFilenameTouched,
    setExportDownloadFilenameTouched,
    setExportColumnKeys,
    setExportDownloadBaseName,
  });

  const {
    exportSortField,
    setExportSortField,
    exportSortDir,
    setExportSortDir,
  } = useCsvExportServerSortInit(open, serverCsvSortConfig);

  const handleDownload = async () => {
    const live =
      downloadFilenameInputRef?.current?.value ?? exportDownloadBaseName;
    await runCsvExportModalDownload({
      exportColumnKeys,
      serverCsvExport,
      serverCsvSortConfig,
      exportSortField,
      exportSortDir,
      excludeCrossSourceNotedRows,
      exportDownloadBaseName: live,
      setServerExporting,
      onOpenChange,
      tab,
      columns,
      rows,
      csvExportWorkDate,
    });
  };

  return {
    templates: tpl.templates,
    templatesBusy: tpl.templatesBusy,
    canManageTemplates,
    exportColumnKeys,
    setExportColumnKeys,
    templateName: tpl.templateName,
    setTemplateName: tpl.setTemplateName,
    templateColor: tpl.templateColor,
    setTemplateColor: tpl.setTemplateColor,
    saveAsDefault: tpl.saveAsDefault,
    setSaveAsDefault: tpl.setSaveAsDefault,
    editingTemplateId: tpl.editingTemplateId,
    serverExporting,
    exportSortField,
    setExportSortField,
    exportSortDir,
    setExportSortDir,
    excludeCrossSourceNotedRows,
    setExcludeCrossSourceNotedRows,
    exportDownloadBaseName,
    onExportDownloadBaseNameChange,
    applyTemplate: tpl.applyTemplate,
    deleteTemplate: tpl.deleteTemplate,
    handleDownload,
    handleSaveTemplate: tpl.handleSaveTemplate,
    handleUpdateTemplate: tpl.handleUpdateTemplate,
  };
}
